import MemberByIdButton from '@/member/components/MemberByIdButton'
import { useOrgContext } from '@/org/contexts/OrgContext'
import useCircleParticipants from '@/participants/hooks/useCircleParticipants'
import CircleSearchInput from '@/search/components/CircleSearchInput'
import MemberSearchButton from '@/search/components/MemberSearchButton'
import { Button, Flex, Input, useToast } from '@chakra-ui/react'
import {
  Meeting_Step_Type_Enum,
  MemberSummaryFragment,
  Thread_Status_Enum,
  useCreateThreadMutation,
} from '@gql'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  ThreadWeather,
  defaultThreadWeather,
} from '@rolebase/shared/model/threadWeather'
import { SearchTypes } from '@rolebase/shared/model/search'
import { useTranslation } from 'react-i18next'
import { CreateIcon } from 'src/icons'
import { threadTypeForStep } from './meetingStepThreadTypes'
import { proposedInitiator } from './proposedInitiator'
import ThreadWeatherBadge from './ThreadWeatherBadge'

interface Props {
  // The meeting's circle: the scope every field is drawn from.
  circleId: string
  stepType: Meeting_Step_Type_Enum
  // Called with the new thread's id, so the step can attach it to itself, and
  // the weather chosen before adding it (#106), which the step writes.
  onCreated(threadId: string, weather: ThreadWeather): void
}

// Depositing a tension during a meeting must not cost more than saying it out
// loud (REU02-A1). The button it replaces opened a modal — one click, one
// context switch, and a form asking for things nobody says in a round table.
//
// 🔑 Why anyone can file for anyone (REU02-A5): in a meeting there is a
// facilitator and a secretary, and often only the secretary touches the screen.
// A form limited to one's own roles would force everyone to grab the keyboard.
// This deliberately differs from the "import an existing tension" picker, which
// stays limited to one's own roles (REUN-02): importing fetches a tension one
// prepared beforehand, creating writes what was just said in the room.
const CLEAR_MEMBER_ID = 'clearMember'

export default function MeetingThreadQuickForm({
  circleId,
  stepType,
  onCreated,
}: Props) {
  const { t } = useTranslation()
  const toast = useToast()
  const { orgId, orgData } = useOrgContext()
  const [createThread] = useCreateThreadMutation()

  // No role at first (Yohan, 21/09, #108): preselecting the meeting's circle
  // meant tensions filed under it by mistake. Once picked, the role stays for
  // the next tension (round table).
  const [roleId, setRoleId] = useState('')
  const [memberId, setMemberId] = useState<string | undefined>()
  // Clicking the vignette opens the list straight away (#108), with « Nobody »
  // on top; closing it without a pick keeps the person already chosen.
  const [pickingMember, setPickingMember] = useState(false)
  const clearMemberItems = useMemo(
    () => [
      {
        id: CLEAR_MEMBER_ID,
        type: SearchTypes.ClearAction,
        title: t('MeetingThreadQuickForm.noMember'),
        text: '',
      },
    ],
    [t]
  )
  const [title, setTitle] = useState('')
  const [weather, setWeather] = useState(defaultThreadWeather)
  const [creating, setCreating] = useState(false)

  // The meeting circle and its direct children — one rule, written once in
  // OrgData, shared with the next step's role picker and the board.
  const scopeCircles = useMemo(
    () => orgData?.circleWithChildren(circleId) ?? [],
    [orgData, circleId]
  )

  // Rings, exactly as the next step's member picker (ORG-06 idiom): the role's
  // holders first, then the circle around it, then the whole organisation —
  // each opened by the footer row of the list, never all at once.
  const [rings, setRings] = useState<1 | 2 | 3>(1)
  useEffect(() => setRings(1), [roleId])

  const roleMembers = useMemo(
    () => (roleId ? orgData?.membersOf(roleId) ?? [] : []),
    [orgData, roleId]
  )
  const parentId = orgData?.getCircle(roleId)?.parentId ?? undefined
  const circleParticipants = useCircleParticipants(parentId, true)

  const scopedMembers = useMemo(() => {
    if (rings >= 3) return undefined // undefined = the whole store
    const seen = new Set<string>()
    const list: MemberSummaryFragment[] = []
    const push = (member: MemberSummaryFragment | undefined) => {
      if (!member || seen.has(member.id)) return
      seen.add(member.id)
      list.push(member)
    }
    roleMembers.forEach((m) => push(m.member))
    if (rings >= 2) circleParticipants.forEach((p) => push(p.member))
    return list
  }, [rings, roleMembers, circleParticipants])

  const memberBottomAction = useMemo(() => {
    if (rings === 1 && parentId)
      return {
        label: t('NextStepContent.seeCircleMembers', {
          count: circleParticipants.length,
        }),
        onClick: () => setRings(2),
      }
    if (rings <= 2)
      return {
        label: t('NextStepContent.seeOrgMembers'),
        onClick: () => setRings(3),
      }
    return undefined
  }, [rings, parentId, circleParticipants.length, t])

  // Picking a role proposes the one person who holds it, and nobody when
  // there is none or several (see proposedInitiator). Recomputed each time the
  // role changes — and only then: `orgData` is refreshed by every change in
  // the org, and re-running on it would overwrite a person picked by hand.
  // `orgData` also guards the first run: without it there are no holders yet,
  // and marking the role as handled would never propose them.
  const lastAutoRoleId = useRef<string>('')
  useEffect(() => {
    if (!orgData || roleId === lastAutoRoleId.current) return
    lastAutoRoleId.current = roleId
    setMemberId(
      roleId ? proposedInitiator(orgData.getLeaders(roleId)) : undefined
    )
  }, [orgData, roleId])

  const handleAdd = async () => {
    const trimmed = title.trim()
    // No error message on an empty title: we are in a meeting, not in an
    // administrative form. The button simply does nothing.
    if (!trimmed || !orgId || !roleId || !memberId || creating) return

    setCreating(true)
    try {
      const { data } = await createThread({
        variables: {
          values: {
            orgId,
            circleId: roleId,
            initiatorMemberId: memberId,
            title: trimmed,
            // The type comes from the step, never from a field (D4): the
            // tensions step creates tactical ones, the governance step
            // governance ones. D1's rule — a type is always explicit — holds,
            // it is simply carried by where one is standing.
            type: threadTypeForStep(stepType),
            // Born ACTIVE, not in preparation (Yohan, 11/08). The column
            // defaults to 'Preparation', which fits a tension one writes at
            // one's desk to bring later. This one is being said out loud right
            // now, in the meeting that will handle it: it has no preparation
            // left to go through.
            status: Thread_Status_Enum.Active,
          },
        },
      })
      const threadId = data?.insert_thread_one?.id
      if (threadId) onCreated(threadId, weather)
      // Only the title and the weather are cleared: it is a round table, the
      // next person often speaks for the same role, and re-picking it every
      // time would be the very friction this form removes (REU02-A6). The
      // weather is not carried over: how much a tension presses belongs to
      // that tension, not to whoever speaks next.
      setTitle('')
      setWeather(defaultThreadWeather)
    } catch (error) {
      // A refusal must be said — the lesson of 10/08, written in
      // NextStepContent and not carried over here at first. Left silent, the
      // button does nothing visible and the person believes they misclicked.
      // The title stays in the field so the attempt can be made again.
      console.error(error)
      toast({ title: t('common.error'), status: 'error', duration: 3000 })
    } finally {
      setCreating(false)
    }
  }

  return (
    <Flex gap={2} align="center" flexWrap="wrap" mb={4}>
      {/* No perimeter selector here (Yohan, 11/08): the list is already the
          meeting's circle and its children — a handful of roles. Partitioning
          them by "my roles / my circles / whole org" says nothing and costs a
          second click to change a role, which is exactly the friction this
          form exists to remove. */}
      <CircleSearchInput
        circles={scopeCircles as any}
        hidePerimeter
        size="sm"
        value={roleId}
        onChange={setRoleId}
      />

      {/* A face rather than a name: the answer to "who brings it" is a person,
          and the vignette costs a quarter of the width of a field. Clicking it
          clears the choice and reopens the list, the same gesture as the next
          step's form. */}
      {memberId && !pickingMember ? (
        <MemberByIdButton
          id={memberId}
          size="sm"
          onClick={() => setPickingMember(true)}
        />
      ) : (
        <MemberSearchButton
          members={scopedMembers}
          bottomAction={memberBottomAction}
          size="sm"
          variant="outline"
          defaultIsOpen={pickingMember}
          additionalTopItems={memberId ? clearMemberItems : undefined}
          onSelect={(id) => {
            setMemberId(id === CLEAR_MEMBER_ID ? undefined : id)
            setPickingMember(false)
          }}
          onClose={() => setPickingMember(false)}
        >
          {t('MeetingThreadQuickForm.member')}
        </MemberSearchButton>
      )}

      {/* Chosen before the tension exists, so it lands classified instead of
          being fixed afterwards (Yohan, 17/09, #106). It still lives in the
          step, never on the tension (REU89-D1). */}
      <ThreadWeatherBadge value={weather} isRound onChange={setWeather} />

      <Input
        size="sm"
        flex={1}
        minW="12em"
        borderRadius="md"
        placeholder={t('MeetingThreadQuickForm.title')}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        // Enter adds, because a keyboard is faster than a mouse when someone is
        // already typing what was just said. `isComposing` guards dictation and
        // composing keyboards, where Enter validates the word being written and
        // must not file a half-typed tension.
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.nativeEvent.isComposing) handleAdd()
        }}
      />

      <Button
        size="sm"
        colorScheme="blue"
        leftIcon={<CreateIcon size={18} />}
        isLoading={creating}
        isDisabled={!roleId}
        onClick={handleAdd}
      >
        {t('MeetingThreadQuickForm.add')}
      </Button>
    </Flex>
  )
}
